import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  ALL_PRODUCTS_LABEL_ID,
  BI,
  EXPERIMENTS,
  LOCALES,
  PAGES,
} from '../../constants';
import { useBI } from '../../hooks/useBI';
import { PageData, PageDataArticle } from '../PageTemplate';
import {
  CustomWindow,
  PageType,
  RoadmapFeatureResolution,
  RoadmapFilterType,
} from '../../types';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import { pageView } from '@wix/bi-logger-new-help-center/v2';
import {
  mapArticleTypeToItemType,
  mapArticleVersionNumberToLabel,
} from '../../utils/bi';
import { FilterToName, sortTypeToName, tabToName } from '../../utils/roadmap';
import { AnalyticsLogger, ArticleSortType, SourceType } from '@wix/answers-api';
import { useExperiments, useHttpClient } from '@wix/fe-essentials-standalone';
import {
  firstAnonymous,
  pageView as pageViewPblc,
} from '@wix/bi-logger-pblc/v2';
import { hasWixClientId, isPersonalPage } from '../../utils/login';

declare const window: CustomWindow;

type PageViewProps = {
  pageType: PageType;
  pageData: PageData;
  itemId?: string;
};

export const PageView: FunctionComponent<PageViewProps> = ({
  pageData,
  pageType,
  itemId,
}) => {
  const { sendBIEvent } = useBI();
  const router = useRouter();
  const { prePageUrl } = useHistoryContext();
  const { locale } = router;
  const { experiments } = useExperiments({ readOnly: true });
  const httpClient = useHttpClient();

  const getArticleFeedbackVersion = (articleType?: number) => {
    if (mapArticleTypeToItemType(articleType) === BI.ITEM_TYPES.ARTICLE) {
      return (
        (experiments.enabled(EXPERIMENTS.SPECS.ARTICLE_FEEDBACK)
          ? 'new'
          : 'old') + '_feedback_form'
      );
    } else {
      return undefined;
    }
  };

  const getReadingTimeVersion = () => 'reading_time_true';

  const articlePageViewItemVersion = (article: PageDataArticle) => {
    const version = [
      getArticleFeedbackVersion(article.type),
      getReadingTimeVersion(),
    ];

    const itemVersions = version.filter(Boolean);
    return itemVersions.length > 0 ? itemVersions.join(', ') : undefined;
  };

  // eslint-disable-next-line complexity
  const sendPageViewEvent = useCallback(async (): Promise<void> => {
    const emptyPageData = Object.keys(pageData).length === 0;
    const urlSearchParams = new URLSearchParams(location.search);
    if (!hasWixClientId()) {
      await sendBIEvent(
        firstAnonymous({
          app_url: `${location.origin}${location.pathname}`,
          _artificial_ts: new Date().getTime(),
          // @ts-expect-error wrong type
          utm_campaign: urlSearchParams.get('utm_campaign'),
          // @ts-expect-error wrong type
          experiment_id: urlSearchParams.get('experiment_id'),
          // @ts-expect-error wrong type
          referral: encodeURIComponent(document.referrer),
          _artifact_name: 'support',
        })
      );
    } else {
      await sendBIEvent(
        pageViewPblc({
          app_url: `${location.origin}${location.pathname}`,
          _artificial_ts: new Date().getTime(),
          // @ts-expect-error wrong type
          utm_campaign: urlSearchParams.get('utm_campaign'),
          // @ts-expect-error wrong type
          experiment_id: urlSearchParams.get('experiment_id'),
          // @ts-expect-error wrong type
          referral: encodeURIComponent(document.referrer),
          _referer: location.href,
          _artifact_name: 'support',
        })
      );
    }
    switch (pageType) {
      case PAGES.ARTICLE:
        if (itemId && pageData?.article) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.ARTICLE,
              kb_lang: locale as string,
              item_id: itemId,
              item_type: mapArticleTypeToItemType(pageData.article.type),
              ab_test_version: mapArticleVersionNumberToLabel(
                pageData.article.alternativeVersionNumber
              ),
              referral_url: prePageUrl,
              item_version: articlePageViewItemVersion(pageData.article),
            })
          );

          // Special for Answers BI
          try {
            AnalyticsLogger.logPageView(
              (url: string, data: unknown) =>
                httpClient.post(`${location.origin}${url}`, data),
              `/api/v1`,
              pageData.article.locale || '',
              SourceType.HELP_CENTER,
              null,
              pageData.article.id || ''
            );
          } catch (e) {
            console.error('Failed to send page view event to Answers BI', e);
          }
        }
        break;
      case PAGES.SEARCH_RESULTS:
        if (
          pageData?.searchResults &&
          pageData?.searchResults.items?.length > 0
        ) {
          const term = new URLSearchParams(location.search).get('term') || '';
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
              kb_lang: locale as string,
              question: term as string,
              presented_content: JSON.stringify(
                pageData?.searchResults.items.map((article, index) => ({
                  order: index + 1,
                  item_id: article.id,
                  locale,
                  type: mapArticleTypeToItemType(article.type),
                }))
              ),
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.CATEGORY:
        if (itemId && pageData.category) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.CATEGORY,
              kb_lang: locale as string,
              item_id: itemId,
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.HOMEPAGE:
        if (emptyPageData) {
          const homePageVersion =
            ((experiments.enabled(EXPERIMENTS.SPECS.NEW_HOMEPAGE) ||
              experiments.enabled(
                EXPERIMENTS.SPECS.ENABLE_WPR_AND_NEW_HOME_PAGE
              )) &&
              locale === LOCALES.EN) ||
            (locale !== LOCALES.EN &&
              experiments.enabled(EXPERIMENTS.SPECS.NEW_HOMEPAGE_ALL_LOCALES));
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.HOME,
              kb_lang: locale as string,
              referral_url: prePageUrl,
              item_version: (homePageVersion ? `new` : `old`) + `_home_page`,
            })
          );
        }
        break;
      case PAGES.TICKET:
        if (itemId && emptyPageData) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.TICKET,
              kb_lang: locale as string,
              item_id: itemId as string,
              item_type: BI.ITEM_TYPES.TICKET,
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.PROFILE:
        if (itemId && emptyPageData) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.PROFILE,
              kb_lang: locale as string,
              item_id: itemId as string,
              item_type: BI.ITEM_TYPES.PROFILE,
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.ROADMAP:
        if (pageData.roadmapData) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.ROADMAP,
              kb_lang: locale as string,
              item_id:
                pageData.roadmapData?.label?.id === ALL_PRODUCTS_LABEL_ID
                  ? undefined
                  : (pageData.roadmapData?.label?.id as string),
              tab: tabToName(
                pageData.roadmapData?.resolution as RoadmapFeatureResolution
              ),
              sort_by: sortTypeToName(
                pageData.roadmapData?.sortType as ArticleSortType
              ),
              filter_by:
                FilterToName[
                  pageData.roadmapData?.filterType as RoadmapFilterType
                ],
              referral_url: prePageUrl,
            })
          );
        }
        break;
      default:
        break;
    }
  }, [locale, pageData, pageType, prePageUrl, itemId]);

  useEffect(() => {
    void (async () => {
      await sendPageViewEvent();
    })();
    const updateHeaderFooterLinks = () => {
      if (window.HeaderAPI) {
        window.HeaderAPI.updateLinks({
          postLogin: location.href,
          postSignOut: isPersonalPage(pageType)
            ? `${location.origin}/${locale}`
            : location.href,
          postSignUp: location.href,
        });
      }
    };

    let updateHeaderFooterLinksInterval: any;
    if (!window.HeaderAPI) {
      updateHeaderFooterLinksInterval = setInterval(() => {
        if (window.HeaderAPI) {
          updateHeaderFooterLinks();
          clearInterval(updateHeaderFooterLinksInterval);
        }
      }, 150);
    } else {
      updateHeaderFooterLinks();
    }
  }, [itemId]);

  return <></>;
};
